import React from "react";
import _ from "lodash";
import { NetworkAPI } from "apis";

import ModalWrapper from "components/common/ModalWrapper";
import ModalFooter from "components/common/ModalFooter";
import NotesInformation from "components/common/NotesInformation";
import NotesEditor from "components/common/NotesEditor";
import LoadingUI from "components/common/LoadingUI";
import ConfigDiffView from "components/common/ConfigDiffView";
import SwitchTabs from "components/common/SwitchTabs";
import ConfigValueReadonly from "components/control-center/auto-pilot/config/ConfigValueReadonly";
import DiffIcon from "components/common/DiffIcon";
import Checkbox from "components/common/Checkbox";
import PromptModal from "components/common/PromptModal";
import NetworkEnsureResourcesModal from "components/ops-mgmt/account/NetworkEnsureResourcesModal";

import { MESSAGE } from "constants/Message";
import {
  buttonModalConfirmClass,
  buttonModalConfirmDisable,
} from "helpers/StyleClass";
import {
  compareTwoObjectsDiff,
  CustomGroupRule,
  CustomRuleAliasKey,
  UprSettingUniqIdMap,
  UprSettingSortedArray,
  getDisplayTypeColor,
} from "helpers/Compare";
import { setValueByPath } from "helpers/Rollback";
import { CUSTOM_TYPE } from "components/ops-mgmt/account/custom-upr/Constants";

const buttonClassBlue =
  "px-2 py-1 bg-blue-400 text-sm rounded border border-gray-400 hover:bg-blue-500 text-white font-semibold";

const buttonClassBlueDisable =
  "px-2 py-1 bg-blue-200 text-sm rounded border border-gray-400 text-white font-semibold cursor-not-allowed";

const TABS = {
  LOCAL: "local",
  SERVER: "server",
  CONFLICT: "conflict",
};

class PreviewValidateDiffModal extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      inputValue: JSON.stringify(props.currentData, null, 4),
      configData: _.cloneDeep(props.currentData),
      serverData: null,
      serverEtag: null,
      serverDiffs: [],
      conflictDiffs: [],

      notesInput: null,

      msg: [],
      isValidated: false,
      isProcessing: false,
      isLoading: false,

      // for diff param
      uniqIdMap: new Map(),
      sortedArray: [],
      diffs: [],
      isConflict: false,

      selectedTab: TABS.LOCAL,

      // for prompt
      isPromptModalOpen: false,
      promptMessage: null,
      promptAction: null,

      // for ensure resources and save
      isEnsureResourcesModalOpen: false,
    };

    this.handlePickChanged = this.handlePickChanged.bind(this);
    this.handleMergeProcess = this.handleMergeProcess.bind(this);
    this.handleValidate = this.handleValidate.bind(this);
    this.handleSaveAndEnsureResources =
      this.handleSaveAndEnsureResources.bind(this);
    this.handleEnsureResourcesClose =
      this.handleEnsureResourcesClose.bind(this);
    this.handleSaveData = this.handleSaveData.bind(this);
    this.handleNotesChanged = this.handleNotesChanged.bind(this);
  }

  async componentDidMount() {
    const { type, networkId, originalRules } = this.props;
    const { configData } = this.state;
    const stateData = {};
    try {
      this.setState({ isLoading: true });
      if (type === CUSTOM_TYPE.UPR) {
        const { customSettings, etag } = await NetworkAPI.getNetworkCustomUpr({
          networkId,
        });

        stateData.serverData = customSettings;
        stateData.serverEtag = etag;
        stateData.uniqIdMap = UprSettingUniqIdMap;
        stateData.sortedArray = UprSettingSortedArray;
        stateData.isConflict = etag !== this.props.etag;
        if (stateData.isConflict) {
          stateData.serverDiffs = compareTwoObjectsDiff(
            originalRules,
            customSettings,
            stateData.uniqIdMap,
            stateData.sortedArray,
          );
        }
      }

      if (!_.isNil(originalRules)) {
        stateData.diffs = compareTwoObjectsDiff(
          originalRules,
          configData,
          stateData.uniqIdMap,
          stateData.sortedArray,
        );
      }

      if (!_.isNil(stateData.serverDiffs) && !_.isNil(stateData.diffs)) {
        const commonDiffs = _.intersectionBy(
          stateData.diffs,
          stateData.serverDiffs,
          "path",
        );

        stateData.conflictDiffs = commonDiffs.map((diff) => ({
          path: diff.path,
          pick: null,
        }));
      }

      this.setState({
        ...stateData,
        isLoading: false,
      });
    } catch (error) {
      this.setState({
        msg: [error.toString(), MESSAGE.ERROR],
        isLoading: false,
      });
    }
  }

  handlePickChanged(path, pick) {
    this.setState((prevState) => {
      const conflictDiffs = prevState.conflictDiffs.map((diff) => {
        if (diff.path === path) {
          return { ...diff, pick: diff.pick === pick ? null : pick };
        }
        return diff;
      });
      return { conflictDiffs };
    });
  }

  handleMergeProcess(isPrompt = true) {
    const {
      conflictDiffs,
      serverDiffs,
      configData,
      serverData,
      uniqIdMap,
      sortedArray,
    } = this.state;
    const { refData, originalRules } = this.props;

    const conflictList = conflictDiffs.map(({ path }) => path);
    const filteredDiffs = serverDiffs.filter(({ path }) => {
      if (!conflictList.includes(path)) return true;

      const conflictDiff = conflictDiffs.find((diff) => diff.path === path);
      return conflictDiff.pick === 1;
    });

    if (isPrompt) {
      this.setState({
        isPromptModalOpen: true,
        promptMessage:
          filteredDiffs.length === 0 ? (
            <>
              All conflict diffs are resolved as local version. Are you sure you
              want to proceed with saving the local version?
            </>
          ) : (
            <>
              Are you sure you want to merge the changes?
              <ConfigDiffView
                diffs={filteredDiffs}
                groupRule={CustomGroupRule}
                refData={refData}
                aliasKey={CustomRuleAliasKey}
              ></ConfigDiffView>
            </>
          ),
        promptAction: () => {
          this.handleMergeProcess(false);
        },
      });
      return;
    }

    let newConfigData = _.cloneDeep(configData);
    filteredDiffs.forEach(({ path }) => {
      try {
        newConfigData = setValueByPath({
          original: serverData,
          changed: newConfigData,
          path,
        });
      } catch (error) {
        console.error(`Error processing path ${path}:`, error);
      }
    });

    const newDiffs = compareTwoObjectsDiff(
      originalRules,
      newConfigData,
      uniqIdMap,
      sortedArray,
    );

    this.setState({
      isPromptModalOpen: false,
      promptMessage: null,
      promptAction: null,
      configData: newConfigData,
      diffs: newDiffs,
      conflictDiffs: [],
      serverDiffs: [],
      selectedTab: TABS.LOCAL,
      inputValue: JSON.stringify(newConfigData, null, 4),
    });
  }

  handleNotesChanged(input) {
    this.setState({ notesInput: input });
  }

  async handleValidate() {
    const { type, handleResetMsg } = this.props;
    const { configData } = this.state;

    if (![CUSTOM_TYPE.FIRST_LOOK, CUSTOM_TYPE.UPR].includes(type)) {
      return;
    }

    if (handleResetMsg) {
      handleResetMsg();
    }

    try {
      this.setState({ isProcessing: true, msg: [] });
      const { isValid, reason } =
        type === CUSTOM_TYPE.FIRST_LOOK
          ? await NetworkAPI.validateCustomFirstLookSetting({
              setting: configData,
            })
          : await NetworkAPI.validateCustomUprSetting({
              setting: configData,
            });

      if (isValid) {
        this.setState({
          msg: ["Validation successful!", ""],
          isValidated: true,
        });
      } else {
        this.setState({
          msg: [reason || "Validation failed!", MESSAGE.ERROR],
        });
      }
    } catch (error) {
      this.setState({
        msg: [error.toString(), MESSAGE.ERROR],
      });
    }
    this.setState({ isProcessing: false });
  }

  handleSaveAndEnsureResources() {
    this.handleSaveData(false);
  }

  handleEnsureResourcesClose() {
    this.setState(
      {
        isEnsureResourcesModalOpen: false,
      },
      () => {
        if (this.props.handleReload) {
          this.props.handleReload();
        }
      },
    );
  }

  handleSaveData(directSave = true) {
    const { isValidated, notesInput, serverEtag, configData } = this.state;
    const { handleSave, hasValidate } = this.props;
    if ((!hasValidate || (hasValidate && isValidated)) && handleSave) {
      handleSave({
        notes: notesInput,
        etag: serverEtag,
        configData,
        callback: !directSave
          ? () => {
              this.setState({
                isEnsureResourcesModalOpen: true,
              });
            }
          : null,
      });
    }
  }

  render() {
    const {
      handleClose,
      type,
      hasValidate = true,
      hasEnsure = false,
      currentData,
      originalRules,
      refData,
      isSaving,
      saveMsg,
    } = this.props;

    const {
      serverDiffs,
      conflictDiffs,
      diffs,
      isConflict,
      selectedTab,
      inputValue,
      isValidated,
      msg,
      isProcessing,
      isLoading,

      isPromptModalOpen,
      promptMessage,
      promptAction,

      isEnsureResourcesModalOpen,
    } = this.state;

    const isConfigDiff = !_.isEqual(currentData, originalRules);
    const isMergeable =
      conflictDiffs.length === 0 ||
      conflictDiffs.every((diff) => diff.pick !== null);

    const conflictPaths = conflictDiffs.map((diff) => diff.path);
    const parsedLocalDiffs = diffs.filter(
      (diff) => !conflictPaths.includes(diff.path),
    );
    const parsedServerDiffs = serverDiffs.filter(
      (diff) => !conflictPaths.includes(diff.path),
    );
    let parsedDiffs = [];
    if (selectedTab === TABS.LOCAL) {
      parsedDiffs = parsedLocalDiffs;
    }

    if (selectedTab === TABS.SERVER) {
      parsedDiffs = parsedServerDiffs;
    }

    const tabs = [
      { value: TABS.LOCAL, label: `Local (${parsedLocalDiffs.length})` },
      {
        value: TABS.SERVER,
        label: `Server (${parsedServerDiffs.length})`,
      },
      {
        value: TABS.CONFLICT,
        label: `Conflict (${conflictDiffs.length})`,
      },
    ];

    let modalTitle = "";
    if (type === CUSTOM_TYPE.FIRST_LOOK) {
      modalTitle = "Preview and Save Custom First Look";
    } else if (type === CUSTOM_TYPE.UPR) {
      modalTitle = "Preview and Save Custom UPR";
    } else if (type === CUSTOM_TYPE.UPR_SEGMENTATION) {
      modalTitle = "Preview and Save UPR Segmentation";
    }

    return (
      <>
        <ModalWrapper
          isOpen={true}
          showCloseFooter={false}
          width="65%"
          title={modalTitle}
          customOverlayStyle={{ zIndex: 100 }}
        >
          {isLoading && (
            <div className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center">
              <LoadingUI iconOnly={true}></LoadingUI>
            </div>
          )}
          <div className="mb-2 flex gap-4 pb-4" style={{ height: "600px" }}>
            <div className="flex w-1/2 flex-col">
              <label
                className={`text-sm font-semibold ${
                  isConfigDiff ? "text-blue-600" : "text-gray-600"
                } `}
              >
                Note
              </label>
              {!isConfigDiff ? (
                <NotesInformation notes={null} />
              ) : (
                <NotesEditor onChanged={this.handleNotesChanged} />
              )}
              <div className="mt-2 flex justify-between text-sm font-semibold">
                <label className="text-gray-600">Config</label>
              </div>

              <div className="border rounded h-full flex-1 overflow-y-auto border-gray-400 bg-gray-100 p-2 text-sm">
                <pre>{inputValue}</pre>
              </div>
            </div>
            <div className="flex w-1/2 flex-col">
              <div className="mb-1 flex justify-end text-sm font-semibold">
                {isConflict && (
                  <>
                    {serverDiffs.length === 0 ? (
                      <label className="text-blue-600">
                        All config version diff detected are resolved and
                        merged.
                      </label>
                    ) : (
                      <label className="text-orange-600">
                        Config version diff detected, please resolve and merge
                        the diff before saving.
                      </label>
                    )}
                  </>
                )}
              </div>

              {serverDiffs.length > 0 && (
                <div className="mb-1 flex justify-between">
                  <SwitchTabs
                    tabs={tabs}
                    selectedTabKey={selectedTab}
                    handleChange={(tab) => this.setState({ selectedTab: tab })}
                  ></SwitchTabs>
                  <button
                    type="button"
                    className={
                      isMergeable ? buttonClassBlue : buttonClassBlueDisable
                    }
                    disabled={!isMergeable}
                    onClick={this.handleMergeProcess}
                  >
                    Merge
                  </button>
                </div>
              )}
              {[TABS.LOCAL, TABS.SERVER].includes(selectedTab) && (
                <ConfigDiffView
                  diffs={parsedDiffs}
                  groupRule={CustomGroupRule}
                  refData={refData}
                  aliasKey={CustomRuleAliasKey}
                  height="calc(100% - 1.5rem)"
                ></ConfigDiffView>
              )}
              {selectedTab === TABS.CONFLICT && (
                <>
                  <div className="mb-1 border-b-2 border-gray-400 pb-1 text-sm font-semibold text-gray-600">
                    <div className="uppercase">
                      Conflict (
                      <span className="font-semibold text-orange-500">
                        {conflictDiffs.length}
                      </span>
                      )
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col gap-2 overflow-y-auto">
                    {conflictDiffs.map(({ path, pick }) => {
                      const localDiff = diffs.find(
                        (diff) => diff.path === path,
                      );
                      const serverDiff = serverDiffs.find(
                        (diff) => diff.path === path,
                      );
                      return (
                        <ConfigConflictItem
                          localDiff={localDiff}
                          serverDiff={serverDiff}
                          path={path}
                          pick={pick}
                          handlePickChanged={this.handlePickChanged}
                          key={`conflict-${path}`}
                        ></ConfigConflictItem>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          <ModalFooter
            msg={saveMsg || msg}
            redo={true}
            isLoading={isProcessing || isSaving}
            onCancel={handleClose}
            confirmText="Save"
            onConfirm={this.handleSaveData}
            disableConfirm={!isValidated && hasValidate}
          >
            {hasValidate && (
              <button
                type="button"
                className={
                  serverDiffs.length === 0
                    ? buttonModalConfirmClass
                    : buttonModalConfirmDisable
                }
                disabled={serverDiffs.length > 0}
                onClick={this.handleValidate}
              >
                Validate
              </button>
            )}
            {hasEnsure && (
              <button
                type="button"
                className={
                  !isValidated && hasValidate
                    ? buttonModalConfirmDisable
                    : buttonModalConfirmClass
                }
                disabled={!isValidated && hasValidate}
                onClick={this.handleSaveAndEnsureResources}
              >
                Save and Ensure Resources
              </button>
            )}
          </ModalFooter>
        </ModalWrapper>

        {isPromptModalOpen && (
          <PromptModal
            header="Merge Confirmation"
            isOpenModal={isPromptModalOpen}
            handleConfirm={promptAction}
            handleCancel={() => this.setState({ isPromptModalOpen: false })}
            width="35%"
          >
            {promptMessage}
          </PromptModal>
        )}

        {hasEnsure && isEnsureResourcesModalOpen && (
          <NetworkEnsureResourcesModal
            networkId={this.props.networkId}
            name={this.props.name}
            handleClose={this.handleEnsureResourcesClose}
          ></NetworkEnsureResourcesModal>
        )}
      </>
    );
  }
}

class ConfigConflictItem extends React.PureComponent {
  render() {
    const { localDiff, serverDiff, path, pick, handlePickChanged } = this.props;
    return (
      <div className="border flex flex-col gap-2 border-gray-400 pb-4 text-sm">
        <div
          className={`break-all px-2 py-1 font-semibold ${
            pick !== null
              ? "bg-blue-200 text-gray-800"
              : "bg-gray-200 text-gray-800"
          }`}
        >
          {localDiff.path}
        </div>
        {[localDiff, serverDiff].map(({ type, from, to }, index) => {
          return (
            <div
              className={`${getDisplayTypeColor(
                type,
              )} border-l-4 px-2 py-1 font-semibold text-gray-800 ${
                pick !== null && pick !== index ? "opacity-25" : ""
              }`}
              key={`conflict-${path}-${index}`}
            >
              <label className="flex cursor-pointer gap-1">
                <Checkbox
                  isChecked={pick === index}
                  onChange={() => handlePickChanged(path, index)}
                ></Checkbox>
                {index === 0 ? "Local" : "Server"}
              </label>

              <div className="flex text-gray-600">
                <div
                  className="flex break-all"
                  style={{ width: "calc(50% - 0.5rem)" }}
                >
                  <div>
                    <ConfigValueReadonly
                      valueType={type}
                      configValue={from}
                    ></ConfigValueReadonly>
                  </div>
                </div>
                <div className="self-center px-2 text-lg">
                  <DiffIcon type={type} />
                </div>

                <div
                  className="flex break-all"
                  style={{ width: "calc(50% - 0.5rem)" }}
                >
                  <div className="font-semibold text-gray-800">
                    <ConfigValueReadonly
                      valueType={type}
                      configValue={to}
                      fromValue={from}
                    ></ConfigValueReadonly>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}

export default PreviewValidateDiffModal;
